import { drop } from "lodash-es";
import { useState, useEffect, forwardRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";

import { captureException } from "extensions/sentry";
// import { getQFilter } from "common/utils";
import { toQueryString } from "common/utils";
import { ErrorView } from "common/components/error";
import { PivotTable } from "common/components/webix/react/PivotTable";
import { getItemFiltersById, setObjectParam } from "common/components/webix/pivot-utils";
import { TablePlaceholder } from "common/components/placeholders";
import { getters } from "common/components/getters/getters";
import { request } from "common/components/request";

import { TOGGLE_FLAGGED_STATUS, TOGGLE_RECONCILED_STATUS } from "common/actions/reconciliation";

import { table_mapper, makeFieldMap } from "./fields";

import configs from "./config.json";

const getSection = function(section) {
    return drop(configs[section], 4); // Drop first four from the original config
};

export const BatchPivotTable = forwardRef(({ dateType } = {}, ref) => {
    const startDate = useSelector((state) => state.common.date_range.startDate);
    const endDate = useSelector((state) => state.common.date_range.endDate);
    const has_nap = useSelector((state) => state.common.merchant_capabilities.has_nap);
    const { qualifiers, section, transferStatus } = useSelector((state) => state.reconciliation);

    const makeStructure = useCallback(() => {
        const values = getSection(section).map(table_mapper(true)).map(
            (val) => ({
                name: val.id,
                operation: "sum",
                format: (value, item, config) => value
            })
        );
        
        return {
            rows: ["stl_currency", "acquirer", dateType],
            values,
            columns: [],
            filters: []
        };
    }, [dateType, section]);

    const getTableFields = useCallback(() => getSection(section).map(({ getter_ref }) => getter_ref) || [], [section]);

    const getTablePivots = useCallback(() => ({
        stl_currency: "Settlement currency",
        acquirer: getters.findPivotName("acquirer"),
        acquiring_entity: getters.findPivotName("acquiring_entity"),
        [dateType]: transferStatus === "accepted" ? "Accepted date" : "Settlement date",
        // eslint-disable-next-line sentence-case/sentence-case
        "funds_transfer_groupings.merchant_id": "Merchant ID",
        // eslint-disable-next-line sentence-case/sentence-case
        // store_id: getters.findPivotName("store_id") || "Store ID", // removed from 1.164 release
        ...has_nap ? {
            // eslint-disable-next-line sentence-case/sentence-case
            "funds_transfer_groupings.company_id": "Company ID",
            "funds_transfer_groupings.bank_statement_narrative": "Bank narrative"
        } : {}
    }), [has_nap, transferStatus, dateType]);

    const [structure, setStructure] = useState(makeStructure());

    const queryParams = toQueryString({
        start: startDate,
        end: endDate,
        status: transferStatus,
        section,
        pivots: structure.rows || ["stl_currency", "acquirer", dateType],
        paginate: false,
        q: qualifiers
    });

    useEffect(() => {
        setStructure(makeStructure());
    }, [section, makeStructure, setStructure]);

    useEffect(() => {
        ref.current?.childElement?.current?.ui?.setStructure(structure);
    }, [ref, structure, section, transferStatus]);

    const eventHandler = ({ type, data } = {}) => {
        try {
            if (["POPUP_APPLIED", "FIELDS_CHANGED"].includes(type)) {
                setStructure({ ...data.newStructure });
            }

            if (["CHANGE_RECONCILED", "CHANGE_FLAGGED"].includes(type)) {
                const item = data.webix.$$("data").getItem(data.obj.row);
                const filters = getItemFiltersById({ id: data.obj.row, pivot: data.webix }).reduce((memo, f) => ({
                    ...memo,
                    [f[0]]: f[1]
                }), {});
                if (type === "CHANGE_RECONCILED") {
                    TOGGLE_RECONCILED_STATUS({ obj: { ...item, ...filters }, keys: Object.keys(filters) })
                        .then(() => { setObjectParam(data.webix.$$("data"), item, { reconciled: !item.reconciled }); });
                }
                
                if (type === "CHANGE_FLAGGED") {
                    TOGGLE_FLAGGED_STATUS({ obj: { ...item, ...filters }, keys: Object.keys(filters) })
                        .then(() => { setObjectParam(data.webix.$$("data"), item, { flagged: !item.flagged }); });
                }
            }
        } catch (error) {
            captureException(error);
        }
    };

    const { data, error, isLoading, isError } = useQuery(
        ["recon_pivot_table", "/merchant/:merchant_ref/processor/reconciliation/v3", queryParams],
        ({
            queryKey: [key, requestUrl, requestQueryParams]
        }) => request([requestUrl, requestQueryParams].join("?")).then(({ results }) => results.map((item) => ({
            ...item,
            "funds_transfer_groupings.merchant_id": item["funds_transfer_groupings.merchant_id"] || "n/a",
            ...has_nap ? {
                "funds_transfer_groupings.company_id": item["funds_transfer_groupings.company_id"] || "n/a",
                "funds_transfer_groupings.bank_transfer_narrative": item["funds_transfer_groupings.bank_transfer_narrative"] || "n/a"
            } : {},
            acquiring_entity: item["acquiring_entity"] || "n/a"
        }))),
        { retry: false }
    );

    const minHeight = (document.getElementsByClassName("recon3-tabs-body")?.[0]?.clientHeight || 430) - 100;

    const renderPivotTable = useMemo(() => (
        <PivotTable
            className="batch-pivot-table"
            key={queryParams}
            ref={ref}
            permissions={false}
            recon3actions
            exportDefaultName={`Batch data by ${section}`}
            ui={{
                structure,
                fieldMap: makeFieldMap(getTablePivots(), getTableFields()),
                datatable: { minHeight }
            }}
            fields={getTableFields()}
            pivots={getTablePivots()}
            eventHandler={eventHandler}
            data={data}
        />
    ), [ref, section, structure, getTablePivots, getTableFields, minHeight, data, queryParams]);

    if (isError) {
        return (
            <ErrorView
                inline={false}
                error={error}
                message="Failed to fetch pivot data"
                custom
                captureInSentry
                hideDetails
            />
        );
    }

    if (isLoading) return <TablePlaceholder columns={1} rows={2} />;

    return renderPivotTable;
});
